import { useQuery } from '@apollo/client/react'
import { useMemo } from 'react'

import { gql } from '../../__generated__/graphql/catalog/gql'
import {
  EmbroideryProviderEmbroideryVariantFieldsFragment,
  EmbroideryVariantKey,
  EmbroideryVariantTag,
  ProductCatalogEmbroideryTypes,
} from '../../__generated__/graphql/catalog/graphql'
import { QueryStatus } from '../../lib/hooks/types'

gql(`
  fragment EmbroideryProviderEmbroideryVariantFields on EmbroideryVariant {
    sku
    tags
    key
    productInfo {
      id
      shopifyId
      sku
      shopifyProductId
      price {
        amount
        currencyCode
      }
      compareAtPrice {
        amount
      }
      group {
        id
        siteWidePromoEligible
        finalSale
        primaryCategory {
          id
          key
        }
        product {
          id
          handle
        }
      }
    }
  }
`)

const getEmbroideryVariantsQuery = gql(`
  query GetEmbroideryVariants {
    embroideryVariants {
      ...EmbroideryProviderEmbroideryVariantFields
    }
  }  
`)

export type EmbroideryVariant = EmbroideryProviderEmbroideryVariantFieldsFragment

/**
 * Every catalog key this app handles. The generated types come from prod's schema, which doesn't
 * list the per-placement text keys yet. Once it does, this is the same as `EmbroideryVariantKey`.
 */
export type EmbroideryCatalogKey = EmbroideryVariantKey | 'TEXT_CHEST' | 'TEXT_SLEEVE'

export const useEmbroideryVariantsQuery = () => {
  const response = useQuery(getEmbroideryVariantsQuery)
  return useMemo(() => {
    const status: QueryStatus = response.loading
      ? 'pending'
      : response.error
      ? 'rejected'
      : 'resolved'
    return {
      data: response.data,
      previousData: response.previousData,
      status,
    }
  }, [response.data, response.error, response.loading, response.previousData])
}

type FilterEmbroideryVariantByTagsProps = {
  tagsAllOf?: EmbroideryVariantTag[]
  tagsOneOf?: EmbroideryVariantTag[]
  tagsNoneOf?: EmbroideryVariantTag[]
}

export const filterEmbroideryVariantByTags = (
  embroideryVariant: EmbroideryProviderEmbroideryVariantFieldsFragment,
  { tagsAllOf, tagsOneOf, tagsNoneOf }: FilterEmbroideryVariantByTagsProps
) => {
  return (
    (tagsAllOf && tagsAllOf.length > 0
      ? tagsAllOf.every(tagsAllOf => embroideryVariant.tags.includes(tagsAllOf))
      : true) &&
    (tagsOneOf && tagsOneOf.length > 0
      ? tagsOneOf.find(tagsOneOf => embroideryVariant.tags.includes(tagsOneOf))
      : true) &&
    (tagsNoneOf && tagsNoneOf.length > 0
      ? !tagsNoneOf.find(tagsNoneOf => embroideryVariant.tags.includes(tagsNoneOf))
      : true)
  )
}

/**
 * The setup (digitization) product. It carries no embroidery type tag, so it never survives a
 * tag filter and has to be found among every variant.
 */
export const findDigitizationVariant = (
  embroideryVariants: readonly EmbroideryProviderEmbroideryVariantFieldsFragment[] | undefined
) => embroideryVariants?.find(embroideryVariant => embroideryVariant.key === 'DIGITIZATION')

// Which products the legacy flow may buy. The per-placement text products carry the same tags as
// `TEXT`, so only their key keeps them out of legacy's tag filters. Listing every key means a new
// one fails to compile until someone decides which flow it belongs to, and a key the catalog
// starts sending before then is left out.
const LEGACY_VARIANT_KEYS: Record<EmbroideryCatalogKey, boolean> = {
  DIGITIZATION: true,
  ICON: true,
  ICON_AND_TEXT: true,
  LOGO_B2B: true,
  LOGO_B2B_AND_TEXT: true,
  LOGO_BW: true,
  LOGO_BW_AND_TEXT: true,
  LOGO_COLOR: true,
  LOGO_COLOR_AND_TEXT: true,
  TEXT: true,
  TEXT_CHEST: false,
  TEXT_SLEEVE: false,
}

export const isLegacyEmbroideryVariant = (
  embroideryVariant: EmbroideryProviderEmbroideryVariantFieldsFragment
) => LEGACY_VARIANT_KEYS[embroideryVariant.key] === true

type FilterEmbroideryVariantProps = {
  withLogo: boolean
  declineColor: boolean
  withIcon: boolean
  withText: boolean
  forB2b: boolean
}

export const filterEmbroideryVariant = (
  embroideryVariant: EmbroideryProviderEmbroideryVariantFieldsFragment,
  { withLogo, declineColor, withIcon, withText, forB2b }: FilterEmbroideryVariantProps
) => {
  if (!isLegacyEmbroideryVariant(embroideryVariant)) return false

  // This seems convoluted, but it helps enforce that we cover our bases. If a new tag is added by the backend,
  // this will require us to consider it in here.
  const requiredTagsMap: Record<EmbroideryVariantTag, boolean> = {
    B2B: forB2b,
    DTC: !forB2b,
    ICON: withIcon,
    TEXT: withText,
    LOGO: withLogo,
    BLACK_AND_WHITE: declineColor,
    DIGITIZATION: false,
    BUNDLE: false,
  }

  const tagsAllOf = Object.entries(requiredTagsMap)
    .filter(entry => entry[1])
    .map(entry => entry[0] as EmbroideryVariantTag)

  const embroideryTypesMap: Record<ProductCatalogEmbroideryTypes, boolean> = {
    ICON: withIcon,
    TEXT: withText,
    LOGO: withLogo,
  }

  // If we are not using any embroidery type, filter it out to avoid addons like Digitization from being a default response
  if (Object.entries(embroideryTypesMap).every(entry => !entry[1])) return false

  const tagsNoneOf = Object.entries(embroideryTypesMap)
    .filter(entry => !entry[1])
    .map(entry => entry[0] as EmbroideryVariantTag)

  if (!declineColor) {
    tagsNoneOf.push('BLACK_AND_WHITE')
  }

  return filterEmbroideryVariantByTags(embroideryVariant, {
    tagsAllOf,
    tagsNoneOf,
  })
}

export const useEmbroideryVariantQuery = (props: FilterEmbroideryVariantProps) => {
  const response = useEmbroideryVariantsQuery()

  const previousData = useMemo(() => {
    const embroideryVariants = response.previousData?.embroideryVariants ?? []
    const matches = embroideryVariants.filter(v => filterEmbroideryVariant(v, props))
    return matches.length === 1 ? matches[0] : undefined
  }, [props, response.previousData?.embroideryVariants])

  const data = useMemo(() => {
    const embroideryVariants = response.data?.embroideryVariants ?? []
    const matches = embroideryVariants.filter(v => filterEmbroideryVariant(v, props))
    return matches.length === 1 ? matches[0] : undefined
  }, [props, response.data?.embroideryVariants])

  return useMemo(() => {
    return {
      ...response,
      data,
      previousData,
    }
  }, [data, previousData, response])
}
